import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { useEffect, useRef, useState } from "react";

function Line({ children, delay = 0, className = "" }) {
  return (
    <span className={`line-mask ${className}`}>
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
        className="block"
      >
        {children}
      </motion.span>
    </span>
  );
}

/** Simulated terminal readout — cycles through pipeline stages */
function TerminalHUD() {
  const stages = [
    "recibiendo tu prenda…",
    "analizando textura y caída…",
    "componiendo modelo, luz y escena…",
    "renderizando en alta resolución…",
    "listo · 3 formatos para descargar",
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % stages.length), 1800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="glass rounded-xl px-4 py-3 flex items-center gap-3 font-mono text-[11px] w-full" data-testid="terminal-hud">
      <span className="text-[rgb(var(--brand))]">›</span>
      <div className="relative flex-1 overflow-hidden h-4">
        {stages.map((s, idx) => (
          <motion.div
            key={idx}
            initial={false}
            animate={{ y: idx === i ? 0 : (idx < i ? -18 : 18), opacity: idx === i ? 1 : 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 text-[rgb(var(--paper-dim))]"
          >
            {s}
          </motion.div>
        ))}
      </div>
      <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--brand))] animate-pulse" />
    </div>
  );
}

export function Hero({ onLaunch }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  // Mouse tilt on card
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-40, 40], [8, -8]), { stiffness: 100, damping: 15 });
  const ry = useSpring(useTransform(mx, [-40, 40], [-10, 10]), { stiffness: 100, damping: 15 });

  useEffect(() => {
    const handler = (e) => {
      const card = document.getElementById("hero-card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      mx.set((e.clientX - cx) / 9);
      my.set((e.clientY - cy) / 9);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [mx, my]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] pt-32 md:pt-36 px-5 md:px-8 pb-16 overflow-hidden"
      data-testid="hero"
    >

      <motion.div style={{ opacity: heroOpacity }} className="relative">
        {/* Meta strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-between mb-10 md:mb-14"
        >
          <span className="tag tag-live" data-testid="hero-status">
            En vivo · Nexeron Studio
          </span>
          <span className="tag hidden md:inline-flex">MAD · 2026</span>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* HEADLINE */}
          <div className="col-span-12 lg:col-span-8">
            <h1
              className="font-display text-[14vw] md:text-[9.4vw] lg:text-[9vw] font-light leading-[0.9] text-[rgb(var(--paper))]"
              data-testid="hero-title"
            >
              <Line delay={0.1}>Tu prenda,</Line>
              <span className="block">
                <Line delay={0.28} className="inline-block">
                  <span className="italic font-serif text-glow" style={{ color: "rgb(var(--brand))" }}>
                    vestida por IA.
                  </span>
                </Line>
              </span>
              <Line delay={0.44}>
                <span className="inline-block relative">
                  Sin producciones.
                </span>
              </Line>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 max-w-xl"
            >
              <p className="text-[rgb(var(--paper-dim))] text-base md:text-lg leading-relaxed">
                Sube la foto de una prenda y obtén modelos vistiéndola en unos
                segundos. Resultados listos para tu tienda online, Instagram o
                lookbook — sin equipo, sin estudio, sin drama.
              </p>

              <div className="mt-8 flex items-center gap-4 flex-wrap">
                <button
                  onClick={onLaunch}
                  className="group relative rounded-full px-6 py-3.5 text-sm font-medium overflow-hidden text-[rgb(var(--ink))]"
                  data-testid="hero-cta"
                >
                  <span className="absolute inset-0 bg-[rgb(var(--brand))] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:bg-[rgb(var(--paper))]" />
                  <span className="relative flex items-center gap-2">
                    Ir al studio
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform group-hover:translate-x-0.5"><path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </button>
                <a
                  href="#trabajo"
                  className="text-sm text-[rgb(var(--paper-dim))] hover:text-[rgb(var(--paper))] link-fx flex items-center gap-2"
                  data-testid="hero-see-work"
                >
                  <span className="font-mono text-[10px] opacity-70">03</span>
                  Ver ejemplos
                </a>
              </div>

              <div className="mt-8">
                <TerminalHUD />
              </div>
            </motion.div>
          </div>

          {/* CARD */}
          <div className="col-span-12 lg:col-span-4">
            <motion.div
              id="hero-card"
              style={{ y, scale, rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden card-tech scanline group"
              data-testid="hero-card"
            >
              <img
                src="https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788960587/acxmebsay90h2vecp02a.png"
                alt="Editorial fashion model"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

              <div className="absolute top-4 left-6 right-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-white/80">
                <span>LOOK · #0142</span>
                <span className="tag-live">live</span>
              </div>
              <div className="absolute bottom-5 left-6 right-6 font-mono text-[11px] text-white">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Resultado</div>
                <div className="text-white text-sm md:text-base tracking-tight mt-0.5">
                  silk_cardigan_oat.png
                </div>
                <div className="mt-2 flex items-center gap-2 text-white/60 text-[10px] uppercase tracking-widest">
                  <span>Seedream 5 Pro</span><span>·</span><span>4K</span><span>·</span><span>3:4</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* footer strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="mt-16 md:mt-24 flex items-end justify-between font-mono text-[10px] md:text-xs uppercase tracking-[0.28em] text-[rgb(var(--paper-dim))]"
        >
          <div className="flex items-center gap-3">
            <span>Scroll</span>
            <span className="inline-block h-[1px] w-10 bg-[rgb(var(--paper-dim))]/60" />
            <span>Descubre cómo funciona</span>
          </div>
          <div className="hidden md:block">§ 001 · Inicio</div>
        </motion.div>
      </motion.div>
    </section>
  );
}
